import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useReaderAuth } from '../../../context/ReaderAuthContext';
import { Check, Loader2, BookOpen, AlertCircle } from 'lucide-react';

const BOOK_SLUG = 'cultural-fluency-without-compromise';

const BookPaymentSuccessPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, activateEbook, hasEbookAccess, isLoading: authLoading } = useReaderAuth();
  
  const [status, setStatus] = useState('activating'); // activating, success, error, needs-signin
  const [error, setError] = useState('');

  useEffect(() => {
    const activateAccess = async () => {
      // Wait for auth to load
      if (authLoading) return;
      
      // If not authenticated, prompt to sign in
      if (!isAuthenticated()) {
        setStatus('needs-signin');
        return;
      }
      
      // If already has access, redirect to library
      if (hasEbookAccess()) {
        setStatus('success');
        setTimeout(() => {
          navigate(`/books/${BOOK_SLUG}/library`);
        }, 2000);
        return;
      }
      
      // Activate ebook access
      try {
        const result = await activateEbook();
        if (result.success) {
          setStatus('success');
          setTimeout(() => {
            navigate(`/books/${BOOK_SLUG}/library`);
          }, 2000);
        } else {
          setError(result.error || 'Failed to activate access');
          setStatus('error');
        }
      } catch (e) {
        setError(e.message || 'An error occurred');
        setStatus('error');
      }
    };
    
    activateAccess();
  }, [authLoading, isAuthenticated, hasEbookAccess, activateEbook, navigate]);

  return (
    <div className="min-h-screen bg-[#faf8f5] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {status === 'activating' && (
          <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
            <Loader2 className="w-16 h-16 mx-auto mb-6 text-[#d4af37] animate-spin" />
            <h1 className="text-2xl font-bold text-[#2c3e50] mb-2">Payment Received</h1>
            <p className="text-[#5a6c7d]">Unlocking your access...</p>
          </div>
        )}
        
        {status === 'success' && (
          <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-[#2c3e50] mb-2">You&apos;re All Set!</h1>
            <p className="text-[#5a6c7d] mb-6">
              Your access to &ldquo;Cultural Fluency Without Compromise&rdquo; is now active.
            </p>
            <p className="text-sm text-[#5a6c7d]">Redirecting to your library...</p>
          </div>
        )}
        
        {status === 'needs-signin' && (
          <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
            <div className="w-20 h-20 bg-[#d4af37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <BookOpen className="w-10 h-10 text-[#d4af37]" />
            </div>
            <h1 className="text-2xl font-bold text-[#2c3e50] mb-2">Payment Received!</h1>
            <p className="text-[#5a6c7d] mb-6">
              Sign in or create an account to access your book.
            </p>
            <div className="space-y-3">
              <Link
                to={`/signin?redirect=/books/${BOOK_SLUG}/payment-success`}
                className="block w-full bg-[#d4af37] text-[#2c3e50] px-6 py-3 rounded-lg font-semibold hover:bg-[#e5c349] transition-colors"
                data-testid="signin-btn"
              >
                Sign In
              </Link>
              <Link
                to={`/signup?redirect=/books/${BOOK_SLUG}/payment-success`}
                className="block w-full border-2 border-[#2c3e50] text-[#2c3e50] px-6 py-3 rounded-lg font-semibold hover:bg-[#2c3e50] hover:text-white transition-colors"
                data-testid="signup-btn"
              >
                Create Account
              </Link>
            </div>
          </div>
        )}
        
        {status === 'error' && (
          <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-[#2c3e50] mb-2">Something Went Wrong</h1>
            <p className="text-[#5a6c7d] mb-6">{error}</p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="block w-full bg-[#d4af37] text-[#2c3e50] px-6 py-3 rounded-lg font-semibold hover:bg-[#e5c349] transition-colors"
              >
                Try Again
              </button>
              <Link
                to="/support"
                className="block w-full border-2 border-[#2c3e50] text-[#2c3e50] px-6 py-3 rounded-lg font-semibold hover:bg-[#2c3e50] hover:text-white transition-colors"
              >
                Contact Support
              </Link>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-8">
          <Link 
            to={`/books/${BOOK_SLUG}`}
            className="text-[#5a6c7d] hover:text-[#2c3e50] transition-colors"
          >
            ← Back to Book
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BookPaymentSuccessPage;
